import { LocalStorageKey } from "@shared/entities";
import { DevLog } from "@shared/Utils";
import axios from "axios";
import { useCallback } from "react";

/**
 * 登入, 登出相關使用的API
 * ※ 本頁不會經過HttpService！
 */
export function useAuthApi() {
  /**
   * 使用者登入
   */
  const Login = useCallback(
    async (
      email: string,
      password: string,
      recaptchaResponse: string,
      userLanguage?: string
    ) => {
      const resp = await axios.post<{
        code?: string;
        message?: string;
        success?: boolean;
        jwt?: string;
        refreshToken?: string;
      }>("/auth/login", {
        email,
        password,
        userLanguage,
        recaptchaResponse,
      });
      DevLog(resp);
      return resp?.data;
    },
    []
  );

  /**
   * 使用 Refresh Token 更新 Refresh Token
   */
  const DoRefreshToken = useCallback(async (refreshToken: string) => {
    return (
      await axios.put<{
        success: boolean;
        refreshToken: string;
      }>("/auth/refresh", {
        refreshToken,
      })
    )?.data;
  }, []);

  /**
   * 使用 Refresh Token 更新 JWT (AccessToken)
   */
  const DoJWTToken = useCallback(async (refreshToken: string) => {
    return (
      await axios.put<{
        success: boolean;
        jwt: string;
      }>("/auth/status", {
        refreshToken,
      })
    )?.data;
  }, []);

  /**
   * 登出
   */
  const Logout = useCallback(async (refreshToken: string) => {
    const token = localStorage.getItem(LocalStorageKey.AccessToken);
    return (
      await axios.post<{
        success: boolean;
      }>("/auth/logout", {
        refreshToken,
        // 這邊要自己加上header, 因為沒有依賴會固定加Header的HttpService
      }, { headers: { Authorization: `Bearer ${token}` } })
    )?.data;
  }, []);

  /**
   * 註冊
   */
  const Register = useCallback(
    async (formValues: {
      userEmail: string;
      userPassword: string;
      userPhone: string;
      userIdentificationNumber: string;
      userRealname: string;
      userNickname: string;
      userBirthday: string;
      userCountry: string;
      userGender: string;
      userLanguage: string;
      recaptchaResponse: string;
    }) => {
      return (
        await axios.post<{
          success: boolean;
        }>("/auth/register", formValues)
      )?.data;
    },
    []
  );
  /**
   * 發送驗證信至使用者信箱，用於驗證使用者信箱
   */
  const SendVerificationMail = useCallback(
    async (userEmail: string, recaptchaResponse: string) => {
      return (
        await axios.post<{
          success: boolean;
        }>("/auth/verification/email/send", {
          userEmail,
          recaptchaResponse,
        })
      )?.data;
    },
    []
  );
  /**
   * 發送重設密碼信至使用者信箱，用於重設使用者密碼
   */
  const VerifyEmail = useCallback(async (userEmailVerificationCode: string) => {
    return (
      await axios.post<{
        success: boolean;
      }>("/auth/verification/email", {
        userEmailVerificationCode,
      })
    )?.data;
  }, []);

  /**
   * 發送驗證簡訊至使用者手機，用於驗證使用者手機
   */
  const SendVerificationSMS = useCallback(
    async (userPhone: string, recaptchaResponse: string) => {
      return (
        await axios.post<{
          success: boolean;
        }>("/auth/verification/sms/send", {
          userPhone,
          recaptchaResponse,
        })
      )?.data;
    },
    []
  );

  /**
   * 驗證使用者手機
   */
  const VerifySMS = useCallback(
    async (
      userEmail: string,
      userSMSVerificationCode: string,
      recaptchaResponse: string
    ) => {
      return (
        await axios.post<{
          success: boolean;
        }>("/auth/verification/sms", {
          userEmail,
          userSMSVerificationCode,
          recaptchaResponse,
        })
      )?.data;
    },
    []
  );

  /**
   * 發送重設密碼信至使用者信箱，用於重設使用者密碼
   */
  const SendResetPasswordMail = useCallback(
    async (userEmail: string, recaptchaResponse: string) => {
      return (
        await axios.post<{
          success: boolean;
        }>("/auth/password/reset/send", {
          userEmail,
          recaptchaResponse,
        })
      )?.data;
    },
    []
  );

  /**
   * 重設使用者密碼
   */
  const ResetPassword = useCallback(
    async (
      userPasswordResetToken: string,
      userPassword: string,
      recaptchaResponse: string
    ) => {
      return (
        await axios.post<{
          success: boolean;
        }>("/auth/password/reset", {
          userPasswordResetToken,
          userPassword,
          recaptchaResponse,
        })
      )?.data;
    },
    []
  );

  /**
   * OAuth 取得認證網址
   */
  const GetOAuthProvider = useCallback(async (provider = 'google') => {
    return (
      await axios.get<{
        success: boolean;
        data: {
          url: string
        }
      }>(`/auth/oauth/${provider}`)
    )?.data;

  }, [])

  /**
 * OAuth 認證登入
 */
  const LoginByOAuth = useCallback(async (code: string, userLanguage: string, provider = 'google') => {
    return (
      await axios.post<{
        code?: string;
        message?: string;
        success?: boolean;
        jwt?: string;
        refreshToken?: string;
      }>(`/auth/oauth/${provider}`, { code, userLanguage })
    )?.data;

  }, [])

  /** 這裡導出可以用的API函數！ */
  return {
    Login,
    DoRefreshToken,
    DoJWTToken,
    Logout,
    Register,
    SendVerificationMail,
    VerifyEmail,
    SendVerificationSMS,
    VerifySMS,
    SendResetPasswordMail,
    ResetPassword,
    GetOAuthProvider,
    LoginByOAuth
  };
}
